import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  BookOpen,
  MessageSquare,
  Settings,
  Filter,
  Headphones,
  Zap,
  Bookmark,
  Share2,
  Copy,
  Send,
  Mic,
  Moon,
  Globe,
  User,
  Heart,
  Trophy,
  Star,
  ChevronDown,
  ChevronUp,
  Wrench,
  HelpCircle,
  Lightbulb,
  Search,
  Volume2,
  Check
} from 'lucide-react';
import {
  SelfImprovement as YogaIcon,
  Shield,
  AutoAwesome,
  Psychology,
  Explore as ExploreIcon,
  Stars as StarsIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';

interface AccordionItemProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden transition-all duration-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            {icon}
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 py-5 bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, color = 'blue' }) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    rose: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    teal: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  };
  return (
    <div className="flex gap-4 items-start">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[color] || colorMap.blue}`}>
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

const DocumentationPage: React.FC = () => {
  const { t } = useTranslation(['documentation', 'seo']);

  return (
    <div className="bg-white dark:bg-gray-900 text-black dark:text-white">
      <SEO
        title={t('seo:documentation.title', { defaultValue: 'Documentation | Sarvalok User Guide' })}
        description={t('seo:documentation.description', { defaultValue: 'Access comprehensive documentation, user guides, and tutorials for maximizing your experience with Sarvalok\'s AI-powered cultural knowledge platform.' })}
        url="/documentation"
      />

      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-8 lg:px-20 py-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 z-0 h-72 w-72 rounded-full bg-gradient-to-br from-orange-300/35 via-amber-300/25 to-blue-300/20 blur-3xl dark:from-orange-400/15 dark:via-amber-400/10 dark:to-blue-400/10" />
          <div className="absolute -bottom-28 -left-28 z-0 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-300/35 via-indigo-300/25 to-purple-300/20 blur-3xl dark:from-blue-400/15 dark:via-indigo-400/10 dark:to-purple-400/10" />
          <img
            src="/decor/sphere3.png"
            alt=""
            className="absolute -top-14 -right-14 z-10 hidden md:block h-auto w-auto max-w-[240px] opacity-90 drop-shadow-2xl"
          />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/40 rounded-full mb-6">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white leading-tight">
            {t('documentation:doc.hero_title', { defaultValue: 'Sarvalok User Guide' })}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {t('documentation:doc.hero_subtitle', { defaultValue: 'Everything you need to explore ancient wisdom, use powerful AI tools, and make the most of the Sarvalok platform.' })}
          </p>
        </div>
      </div>

      {/* Quick Start Section */}
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('documentation:doc.quick_start_title', { defaultValue: 'Quick Start' })}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t('documentation:doc.quick_start_desc', { defaultValue: 'Get up and running in minutes.' })}</p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('documentation:doc.step1_title', { defaultValue: 'Create an Account' })}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('documentation:doc.step1_desc', { defaultValue: 'Sign up with your email to begin your journey with Sarvalok.' })}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('documentation:doc.step2_title', { defaultValue: 'Ask a Question' })}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('documentation:doc.step2_desc', { defaultValue: 'Type your question in the chat box. Be descriptive for the best results.' })}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('documentation:doc.step3_title', { defaultValue: 'Explore & Learn' })}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('documentation:doc.step3_desc', { defaultValue: 'Dive into answers, explore related topics, and bookmark what resonates with you.' })}</p>
            </div>
          </div>
        </div>

        {/* Tutorial Video */}
        <div className="mb-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            {t('documentation:doc.tutorial_title', { defaultValue: 'Video Tutorial' })}
          </h3>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
              src="https://www.youtube.com/embed/QNBmAQ7HCXo"
              title="Sarvalok - Explainer & Usage Tutorial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 italic">
            {t('documentation:doc.tutorial_desc', { defaultValue: 'Watch our comprehensive tutorial to get started with Sarvalok quickly and effectively.' })}
          </p>
        </div>

        {/* Main Documentation Sections */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('documentation:doc.features_title', { defaultValue: 'Features & Tools' })}</h2>
        <div className="space-y-4 mb-12">

          {/* Heritage AI Chat */}
          <AccordionItem
            title={t('documentation:doc.heritage_chat_title', { defaultValue: 'Heritage AI Chat' })}
            icon={<MessageSquare className="w-5 h-5" />}
            defaultOpen={true}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.heritage_chat_desc', { defaultValue: 'The core of Sarvalok is an AI-powered chat interface specializing in ancient Indian wisdom, philosophy, theology, history, and cultural knowledge. Ask questions in natural language and receive detailed, well-researched answers drawn from authentic sources and traditions.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<Send className="w-5 h-5" />}
                title={t('documentation:doc.asking_title', { defaultValue: 'Asking Questions' })}
                description={t('documentation:doc.asking_desc', { defaultValue: 'Type your question in the chat box and press Send. Be descriptive for better results — for example, "What is the concept of Dharma according to the Bhagavad Gita?" yields richer answers than simply "What is Dharma?".' })}
                color="blue"
              />
              <FeatureCard
                icon={<Mic className="w-5 h-5" />}
                title={t('documentation:doc.stt_title', { defaultValue: 'Speech-to-Text Input' })}
                description={t('documentation:doc.stt_desc', { defaultValue: 'Use the microphone button next to the chat input to speak your question. Your speech is transcribed to text automatically, making it easy to ask questions hands-free.' })}
                color="purple"
              />
              <FeatureCard
                icon={<Volume2 className="w-5 h-5" />}
                title={t('documentation:doc.tts_title', { defaultValue: 'Text-to-Speech (Voice Playback)' })}
                description={t('documentation:doc.tts_desc', { defaultValue: 'Each answer includes a speaker button that reads the response aloud using text-to-speech. Listen to answers while you meditate, walk, or simply prefer audio learning.' })}
                color="teal"
              />
              <FeatureCard
                icon={<Copy className="w-5 h-5" />}
                title={t('documentation:doc.copy_title', { defaultValue: 'Copy, Share & Resend' })}
                description={t('documentation:doc.copy_desc', { defaultValue: 'Every response has action buttons: Copy to clipboard, Share via Facebook, Twitter, WhatsApp, or Telegram, and Resend to regenerate the answer if needed.' })}
                color="green"
              />
              <FeatureCard
                icon={<Bookmark className="w-5 h-5" />}
                title={t('documentation:doc.bookmarks_title', { defaultValue: 'Bookmarks' })}
                description={t('documentation:doc.bookmarks_desc', { defaultValue: 'Save meaningful answers by clicking the bookmark icon on any response. Access all your bookmarks from the sidebar under the Bookmarks tab for quick reference anytime.' })}
                color="amber"
              />
              <FeatureCard
                icon={<ExploreIcon className="w-5 h-5" />}
                title={t('documentation:doc.explore_topics_title', { defaultValue: 'Related Topics to Explore' })}
                description={t('documentation:doc.explore_topics_desc', { defaultValue: 'After each answer, Sarvalok suggests related topics you can click to continue your exploration. These contextually relevant suggestions help you dive deeper into interconnected ideas and concepts.' })}
                color="indigo"
              />
              <FeatureCard
                icon={<MessageSquare className="w-5 h-5" />}
                title={t('documentation:doc.threads_title', { defaultValue: 'Chat History & Threads' })}
                description={t('documentation:doc.threads_desc', { defaultValue: 'Your conversations are organized into threads accessible from the sidebar. You can rename, delete, or switch between threads. Start new conversations anytime while preserving your previous discussions.' })}
                color="blue"
              />
            </div>
          </AccordionItem>

          {/* Modes & Filters */}
          <AccordionItem
            title={t('documentation:doc.modes_title', { defaultValue: 'Modes & Filters' })}
            icon={<Filter className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.modes_desc', { defaultValue: 'Customize how Sarvalok responds to your queries using powerful modes and filters. These tools give you control over the depth, speed, and source of your answers.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<Shield className="w-5 h-5" />}
                title={t('documentation:doc.citation_title', { defaultValue: 'Citation Mode vs. Normal Mode' })}
                description={t('documentation:doc.citation_desc', { defaultValue: 'Toggle between Citation Mode (source-verified answers with references to specific texts and manuscripts) and Normal Mode (tradition-based answers for general exploration). Use the vertical toggle switch next to the input box.' })}
                color="blue"
              />
              <FeatureCard
                icon={<Settings className="w-5 h-5" />}
                title={t('documentation:doc.authority_title', { defaultValue: 'Authority Range (0–10)' })}
                description={t('documentation:doc.authority_desc', { defaultValue: 'Adjust the authority slider to control the depth and scholarly level of responses. Range 0–4 provides general knowledge, 5–8 offers scholarly interpretations, and 9–10 draws from Rishi and Sage perspectives with the deepest textual authority.' })}
                color="orange"
              />
              <FeatureCard
                icon={<Filter className="w-5 h-5" />}
                title={t('documentation:doc.source_filters_title', { defaultValue: 'Source Filters (Shastra Categories)' })}
                description={t('documentation:doc.source_filters_desc', { defaultValue: 'Select specific knowledge categories to focus your search. Available categories include Arthashastra (Governance & Economics), Dharmashastra (Law & Ethics), Vedanta (Philosophy), Yogashastra (Yoga), Ayurveda (Medicine), Jyotishashastra (Astronomy), Ganitashastra (Mathematics), Silpashastra (Architecture & Arts), Rasayana (Alchemy & Chemistry), and Tantra (Esoteric traditions).' })}
                color="purple"
              />
              <FeatureCard
                icon={<Headphones className="w-5 h-5" />}
                title={t('documentation:doc.listening_title', { defaultValue: 'Listening Mode' })}
                description={t('documentation:doc.listening_desc', { defaultValue: 'Enable Listening Mode (headphones icon) for an audio-optimized experience. In this mode, responses are crafted for spoken delivery and automatically played back via text-to-speech. Ideal for passive learning and contemplation.' })}
                color="teal"
              />
              <FeatureCard
                icon={<Zap className="w-5 h-5" />}
                title={t('documentation:doc.fast_mode_title', { defaultValue: 'Fast Mode' })}
                description={t('documentation:doc.fast_mode_desc', { defaultValue: 'Toggle Fast Mode (lightning icon) for quicker, more concise responses. When disabled, answers are more comprehensive and detailed. Use Fast Mode when you need quick facts, and disable it for in-depth research.' })}
                color="amber"
              />
            </div>
          </AccordionItem>

          {/* Astro Mode */}
          <AccordionItem
            title={t('documentation:doc.astro_title', { defaultValue: 'Astro Mode & Compatibility' })}
            icon={<StarsIcon className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.astro_desc', { defaultValue: 'Sarvalok\'s Astro Mode offers personalized Vedic astrological insights based on your birth details. These features are available for premium subscribers.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<User className="w-5 h-5" />}
                title={t('documentation:doc.astro_mode_title', { defaultValue: 'Personal Horoscope (Astro Mode)' })}
                description={t('documentation:doc.astro_mode_desc', { defaultValue: 'Enter your birth date, time, and location to generate your personal Vedic birth chart. Once generated, you can ask personalized astrological questions and receive insights tailored to your chart, including planetary positions, house analysis, and dasha periods.' })}
                color="purple"
              />
              <FeatureCard
                icon={<Heart className="w-5 h-5" />}
                title={t('documentation:doc.compatibility_title', { defaultValue: 'Astro Compatibility' })}
                description={t('documentation:doc.compatibility_desc', { defaultValue: 'Compare two birth charts for marriage and relationship compatibility analysis. Enter the birth details of both individuals to receive a comprehensive compatibility report based on traditional Vedic ashtakoot matching and additional factors.' })}
                color="rose"
              />
              <FeatureCard
                icon={<AutoAwesome className="w-5 h-5" />}
                title={t('documentation:doc.astro_modes_title', { defaultValue: 'Guidance vs. Detailed Analysis' })}
                description={t('documentation:doc.astro_modes_desc', { defaultValue: 'In Astro Mode, toggle between Guidance mode (direct, actionable predictions for your life without technical jargon) and Detailed mode (in-depth analysis of houses, karakas, dashas, and technical astrological concepts).' })}
                color="indigo"
              />
            </div>
          </AccordionItem>

          {/* Interactive Features */}
          <AccordionItem
            title={t('documentation:doc.interactive_title', { defaultValue: 'Interactive Features' })}
            icon={<Trophy className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.interactive_desc', { defaultValue: 'Beyond chat, Sarvalok offers several interactive experiences to enrich your learning journey.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<BookOpen className="w-5 h-5" />}
                title={t('documentation:doc.journey_title', { defaultValue: 'Journey into Wisdom' })}
                description={t('documentation:doc.journey_desc', { defaultValue: 'A guided multi-step experience that takes you through key texts like the Bhagavad Gita, Ramayana, and Yoga Vasistha. Each step presents curated insights, making it perfect for newcomers who want an introduction to ancient Indian literature.' })}
                color="orange"
              />
              <FeatureCard
                icon={<YogaIcon className="w-5 h-5" />}
                title={t('documentation:doc.pranayama_title', { defaultValue: 'Pranayama Practice' })}
                description={t('documentation:doc.pranayama_desc', { defaultValue: 'An interactive guided breathing exercise tool. Follow along with timed breathing patterns for pranayama practice, helping you integrate mindfulness and wellness into your learning sessions.' })}
                color="green"
              />
              <FeatureCard
                icon={<Trophy className="w-5 h-5" />}
                title={t('documentation:doc.quiz_title', { defaultValue: 'Indic Knowledge Quiz' })}
                description={t('documentation:doc.quiz_desc', { defaultValue: 'Test your knowledge of Indian history, philosophy, and culture with interactive quizzes. Questions are generated based on the topics you have been exploring. After completing a quiz, there is a cooldown period before you can take another.' })}
                color="amber"
              />
              <FeatureCard
                icon={<Check className="w-5 h-5" />}
                title={t('documentation:doc.questlog_title', { defaultValue: 'Questlog (To-Do Manager)' })}
                description={t('documentation:doc.questlog_desc', { defaultValue: 'A personal task manager to track your learning goals and spiritual practices. Create, organize, and check off items as you progress through your studies.' })}
                color="blue"
              />
              <FeatureCard
                icon={<Search className="w-5 h-5" />}
                title={t('documentation:doc.suggested_title', { defaultValue: 'Suggested Questions & Categories' })}
                description={t('documentation:doc.suggested_desc', { defaultValue: 'The welcome screen displays clickable knowledge categories (Arthashastra, Vedanta, Ayurveda, etc.) with curated sample questions. Click any category to browse pre-set questions, or click a question to instantly start exploring that topic.' })}
                color="indigo"
              />
            </div>
          </AccordionItem>

          {/* Explore Menu */}
          <AccordionItem
            title={t('documentation:doc.explore_menu_title', { defaultValue: 'Explore' })}
            icon={<ExploreIcon className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.explore_menu_desc', { defaultValue: 'The Explore menu in the navigation bar gives you access to specialized tools and external Sarvalok products.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<BookOpen className="w-5 h-5" />}
                title={t('documentation:doc.rigveda_title', { defaultValue: 'Rig Veda Explorer' })}
                description={t('documentation:doc.rigveda_desc', { defaultValue: 'Browse, search, and study the Rig Veda with an interactive explorer tool. Navigate through hymns, view original Sanskrit text alongside translations, and gain deeper understanding of one of the oldest known texts in human history.' })}
                color="orange"
              />
              <FeatureCard
                icon={<Volume2 className="w-5 h-5" />}
                title={t('documentation:doc.chant_title', { defaultValue: 'Chant' })}
                description={t('documentation:doc.chant_desc', { defaultValue: 'Explore and listen to sacred chants and mantras from various Vedic and Indian traditions. An immersive audio experience for spiritual practice and study.' })}
                color="purple"
              />
              <FeatureCard
                icon={<Psychology className="w-5 h-5" />}
                title={t('documentation:doc.experimental_title', { defaultValue: 'Experimental Tools' })}
                description={t('documentation:doc.experimental_desc', { defaultValue: 'Access advanced experimental features including Terminology Lookup (find precise definitions and meanings of Sanskrit and Indian philosophical terms) and Deep Inquiry (submit complex research questions for comprehensive, multi-source analysis).' })}
                color="teal"
              />
            </div>
          </AccordionItem>

          {/* Global Risk Radar */}
          <AccordionItem
            title={t('documentation:doc.global_risk_title', { defaultValue: 'Global Risk Radar' })}
            icon={<Globe className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.global_risk_desc', { defaultValue: 'The Global Risk Radar provides astrological insights into world events and geopolitical dynamics. Select a location and date to view risk assessments based on planetary transits and traditional Jyotish analysis. You can also ask follow-up questions about the generated report.' })}
            </p>
          </AccordionItem>

          {/* AI Suite */}
          <AccordionItem
            title={t('documentation:doc.ai_suite_title', { defaultValue: 'AI Suite (Tools)' })}
            icon={<Wrench className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.ai_suite_desc', { defaultValue: 'The AI Suite is a collection of general-purpose AI productivity tools available to all logged-in members. Access the full dashboard from the "Tools" button on the chat page or via the navigation menu.' })}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: <MessageSquare className="w-4 h-4" />, title: t('documentation:doc.tool_chat', { defaultValue: 'AI Chat Assistant' }), desc: t('documentation:doc.tool_chat_desc', { defaultValue: 'General-purpose AI conversation for any topic.' }) },
                { icon: <BookOpen className="w-4 h-4" />, title: t('documentation:doc.tool_writer', { defaultValue: 'Content Writer' }), desc: t('documentation:doc.tool_writer_desc', { defaultValue: 'Generate articles, essays, and creative content.' }) },
                { icon: <Wrench className="w-4 h-4" />, title: t('documentation:doc.tool_code', { defaultValue: 'Code Generator' }), desc: t('documentation:doc.tool_code_desc', { defaultValue: 'Write, debug, and explain code in multiple languages.' }) },
                { icon: <BookOpen className="w-4 h-4" />, title: t('documentation:doc.tool_summarizer', { defaultValue: 'Document Summarizer' }), desc: t('documentation:doc.tool_summarizer_desc', { defaultValue: 'Condense long documents into key points.' }) },
                { icon: <Check className="w-4 h-4" />, title: t('documentation:doc.tool_grammar', { defaultValue: 'Grammar Check' }), desc: t('documentation:doc.tool_grammar_desc', { defaultValue: 'Fix grammar, spelling, and improve writing clarity.' }) },
                { icon: <BookOpen className="w-4 h-4" />, title: t('documentation:doc.tool_story', { defaultValue: 'Story Generator' }), desc: t('documentation:doc.tool_story_desc', { defaultValue: 'Create engaging stories and narratives.' }) },
                { icon: <Send className="w-4 h-4" />, title: t('documentation:doc.tool_email', { defaultValue: 'Email Assistant' }), desc: t('documentation:doc.tool_email_desc', { defaultValue: 'Draft professional and personal emails.' }) },
                { icon: <Globe className="w-4 h-4" />, title: t('documentation:doc.tool_translator', { defaultValue: 'Translator' }), desc: t('documentation:doc.tool_translator_desc', { defaultValue: 'Translate text between multiple languages.' }) },
                { icon: <Share2 className="w-4 h-4" />, title: t('documentation:doc.tool_social', { defaultValue: 'Social Media Assistant' }), desc: t('documentation:doc.tool_social_desc', { defaultValue: 'Create engaging social media content and posts.' }) },
                { icon: <BookOpen className="w-4 h-4" />, title: t('documentation:doc.tool_meeting', { defaultValue: 'Meeting Notes' }), desc: t('documentation:doc.tool_meeting_desc', { defaultValue: 'Generate structured meeting notes and summaries.' }) },
                { icon: <User className="w-4 h-4" />, title: t('documentation:doc.tool_resume', { defaultValue: 'Resume Builder' }), desc: t('documentation:doc.tool_resume_desc', { defaultValue: 'Create professional resumes with AI optimization.' }) },
                { icon: <HelpCircle className="w-4 h-4" />, title: t('documentation:doc.tool_interview', { defaultValue: 'Interview Prep' }), desc: t('documentation:doc.tool_interview_desc', { defaultValue: 'Prepare with AI-generated interview questions and tips.' }) },
                { icon: <Search className="w-4 h-4" />, title: t('documentation:doc.tool_ocr', { defaultValue: 'OCR Tool' }), desc: t('documentation:doc.tool_ocr_desc', { defaultValue: 'Extract and analyze text from images and documents.' }) },
                { icon: <Star className="w-4 h-4" />, title: t('documentation:doc.tool_sentiment', { defaultValue: 'Sentiment Analysis' }), desc: t('documentation:doc.tool_sentiment_desc', { defaultValue: 'Analyze sentiment and emotions in text content.' }) },
                { icon: <Trophy className="w-4 h-4" />, title: t('documentation:doc.tool_quiz', { defaultValue: 'Quiz Generator' }), desc: t('documentation:doc.tool_quiz_desc', { defaultValue: 'Create interactive quizzes and assessments.' }) },
                { icon: <Wrench className="w-4 h-4" />, title: t('documentation:doc.tool_sql', { defaultValue: 'SQL Builder' }), desc: t('documentation:doc.tool_sql_desc', { defaultValue: 'Generate SQL queries from natural language.' }) },
                { icon: <Lightbulb className="w-4 h-4" />, title: t('documentation:doc.tool_recipe', { defaultValue: 'Recipe Generator' }), desc: t('documentation:doc.tool_recipe_desc', { defaultValue: 'Create recipes with AI culinary expertise.' }) },
                { icon: <Star className="w-4 h-4" />, title: t('documentation:doc.tool_finance', { defaultValue: 'Finance Assistant' }), desc: t('documentation:doc.tool_finance_desc', { defaultValue: 'Get AI-powered financial insights and advice.' }) },
                { icon: <Star className="w-4 h-4" />, title: t('documentation:doc.tool_image', { defaultValue: 'Image Generator' }), desc: t('documentation:doc.tool_image_desc', { defaultValue: 'Generate images from text descriptions using AI.' }) },
              ].map((tool, idx) => (
                <div key={idx} className="flex gap-3 items-start p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="w-8 h-8 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                    {tool.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-gray-900 dark:text-white">{tool.title}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{tool.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </AccordionItem>

          {/* AI Studio: hidden for Indian/Razorpay users — they don't get Weave access */}
          {!(typeof window !== 'undefined' && Boolean((window as any).__isIndianLocale__)) && (
            <AccordionItem
              title={t('documentation:doc.ai_studio_title', { defaultValue: 'AI Studio' })}
              icon={<AutoAwesome className="w-5 h-5" />}
            >
              <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
                {t('documentation:doc.ai_studio_desc', { defaultValue: 'AI Studio is Sarvalok\'s integrated AI studio platform. Logged-in members can seamlessly access AI Studio via the "AI Studio" link in the navigation bar. You are automatically signed in using your Sarvalok account — no separate registration needed. AI Studio offers its own set of advanced AI capabilities and creative tools.' })}
              </p>
            </AccordionItem>
          )}

          {/* Platform Settings */}
          <AccordionItem
            title={t('documentation:doc.platform_title', { defaultValue: 'Platform & Settings' })}
            icon={<Settings className="w-5 h-5" />}
          >
            <p className="text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">
              {t('documentation:doc.platform_desc', { defaultValue: 'Customize your Sarvalok experience with the following platform-wide settings and features.' })}
            </p>
            <div className="space-y-5">
              <FeatureCard
                icon={<Moon className="w-5 h-5" />}
                title={t('documentation:doc.dark_mode_title', { defaultValue: 'Dark Mode / Light Mode' })}
                description={t('documentation:doc.dark_mode_desc', { defaultValue: 'Toggle between dark and light themes using the sun/moon icon in the navigation bar. Your preference is saved and persists across sessions.' })}
                color="indigo"
              />
              <FeatureCard
                icon={<Globe className="w-5 h-5" />}
                title={t('documentation:doc.language_title', { defaultValue: 'Multi-Language Support' })}
                description={t('documentation:doc.language_desc', { defaultValue: 'Sarvalok is available in 17 languages including English, Hindi, Tamil, Telugu, Japanese, Chinese, German, French, Spanish, Portuguese, Russian, Dutch, Polish, Indonesian, Nepali, Hebrew, and more. Use the language switcher in the navigation bar to change the interface language. You can also ask questions and receive answers in your preferred language.' })}
                color="green"
              />
              <FeatureCard
                icon={<User className="w-5 h-5" />}
                title={t('documentation:doc.account_title', { defaultValue: 'Account & Settings' })}
                description={t('documentation:doc.account_desc', { defaultValue: 'Access your account settings to view your profile, current plan, usage statistics, and subscription details. You can also manage your password, log out, and sync your subscription status from the Settings page.' })}
                color="blue"
              />
              <FeatureCard
                icon={<Star className="w-5 h-5" />}
                title={t('documentation:doc.plans_title', { defaultValue: 'Subscription Plans' })}
                description={t('documentation:doc.plans_desc', { defaultValue: 'Sarvalok offers a free plan with limited usage and premium plans with expanded features. Visit the Pricing page to compare plans, upgrade, or manage your subscription. Premium features include Astro Mode, extended usage limits, and priority access.' })}
                color="amber"
              />
              <FeatureCard
                icon={<Settings className="w-5 h-5" />}
                title={t('documentation:doc.cancel_title', { defaultValue: 'Canceling Your Subscription' })}
                description={t('documentation:doc.cancel_desc', { defaultValue: 'To cancel, go to Settings → Subscription and click “Cancel subscription”. Your access stays active until the end of your current billing period. If you cancel and change your mind, you may be able to resume from Settings before it ends; otherwise you can subscribe again from the Pricing page.' })}
                color="rose"
              />
            </div>
          </AccordionItem>

          {/* Tips for Best Results */}
          <AccordionItem
            title={t('documentation:doc.tips_title', { defaultValue: 'Tips for Best Results' })}
            icon={<Lightbulb className="w-5 h-5" />}
          >
            <div className="space-y-4">
              {[
                { title: t('documentation:doc.tip1_title', { defaultValue: 'Be Specific:' }), desc: t('documentation:doc.tip1_desc', { defaultValue: 'Instead of broad questions, specify the text, tradition, or context you\'re interested in. "Explain karma according to Yoga Sutras" gives better results than "What is karma?".' }) },
                { title: t('documentation:doc.tip2_title', { defaultValue: 'Use Filters:' }), desc: t('documentation:doc.tip2_desc', { defaultValue: 'If you\'re researching a specific discipline, select the relevant Shastra category filter to focus the AI\'s knowledge retrieval on that domain.' }) },
                { title: t('documentation:doc.tip3_title', { defaultValue: 'Try Citation Mode:' }), desc: t('documentation:doc.tip3_desc', { defaultValue: 'When you need verified, source-referenced answers, enable Citation Mode. This is especially useful for academic research or when you want to trace information back to specific texts.' }) },
                { title: t('documentation:doc.tip4_title', { defaultValue: 'Adjust Authority:' }), desc: t('documentation:doc.tip4_desc', { defaultValue: 'Set a higher authority range (8–10) for deep, scripture-level insights, or a lower range (0–4) for accessible, introductory-level explanations.' }) },
                { title: t('documentation:doc.tip5_title', { defaultValue: 'Start New Threads:' }), desc: t('documentation:doc.tip5_desc', { defaultValue: 'For unrelated topics, start a new conversation thread. This helps the AI maintain context and provide more relevant answers.' }) },
              ].map((tip, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0 text-sm font-bold mt-0.5">{idx + 1}</div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    <strong>{tip.title}</strong> {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </AccordionItem>

          {/* Limitations */}
          <AccordionItem
            title={t('documentation:doc.limitations_title', { defaultValue: 'Limitations & Disclaimers' })}
            icon={<HelpCircle className="w-5 h-5" />}
          >
            <div className="space-y-3">
              {[
                t('documentation:doc.limit_1', { defaultValue: 'Sarvalok does not perform rituals, pujas, or any form of worship.' }),
                t('documentation:doc.limit_2', { defaultValue: 'Astrological features provide insights based on Vedic traditions and are not intended as definitive life guidance.' }),
                t('documentation:doc.limit_3', { defaultValue: 'The platform does not provide initiations (diksha) or spiritual ordinations.' }),
                t('documentation:doc.limit_4', { defaultValue: 'Responses are not medical, legal, or financial advice. Always consult qualified professionals.' }),
                t('documentation:doc.limit_5', { defaultValue: 'While Sarvalok strives for accuracy using authentic sources, we recommend verifying important information with trusted references.' }),
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">{item}</p>
                </div>
              ))}
            </div>
          </AccordionItem>
        </div>

        {/* Contact Footer */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            {t('documentation:doc.help_title', { defaultValue: 'Need More Help?' })}
          </h2>
          <p className="text-blue-200 mb-6 max-w-2xl mx-auto">
            {t('documentation:doc.help_desc', { defaultValue: 'If you have questions not covered here, or need assistance with any feature, our support team is happy to help.' })}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:support@sarvalok.com"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-colors backdrop-blur-sm border border-white/20"
            >
              <Send className="w-4 h-4" />
              support@sarvalok.com
            </a>
            <a
              href="https://wa.me/918125831103"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600/80 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t('documentation:doc.whatsapp_btn', { defaultValue: 'Chat on WhatsApp' })}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
